<div class="w-full">
    <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div class="min-w-0 flex-[1_1_18rem]">
            <h1 class="m-0 text-xl font-bold leading-7 text-slate-900">Iscrizioni</h1>
            <p class="m-0 mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                Andamento delle iscrizioni per categoria. Apri una categoria per vedere gli atleti.
            </p>
        </div>
        <div class="flex flex-wrap items-center gap-3">
            <label class="m-0 inline-flex items-center gap-2 text-sm font-semibold text-slate-500">
                <i class="bi bi-calendar3" aria-hidden="true"></i>
                <span class="sr-only">Stagione</span>
                <select class="form-select form-select-solid !w-auto min-w-[14rem]"
                        [ngModel]="season?.id" (ngModelChange)="selectSeason($event)" [disabled]="!seasons.length">
                    <option *ngFor="let item of seasons" [ngValue]="item.id">
                        {{ item.name }} · {{ seasonPhaseLabel(item) }}
                    </option>
                </select>
            </label>
            <a class="btn welldone-btn-gray inline-flex shrink-0 items-center gap-1.5" [routerLink]="['/companies/categories']">
                <i class="bi bi-gear" aria-hidden="true"></i>
                Gestisci categorie
            </a>
        </div>
    </header>

    <div *ngIf="season && !isCurrentSeason"
         class="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
        <i class="bi bi-clock-history text-lg" aria-hidden="true"></i>
        <span class="min-w-0 flex-1">
            Stai guardando la stagione <b>{{ season.name }}</b> ({{ seasonPhaseLabel(season) | lowercase }}). Le nuove iscrizioni usano la stagione corrente.
        </span>
        <button type="button" class="btn btn-reset-link !p-0 font-semibold" (click)="selectSeason('')">Torna alla stagione corrente</button>
    </div>

    <!-- No current season / error -->
    <div *ngIf="!loading && !season && !loadError"
         class="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
        <i class="bi bi-calendar-x mb-3 text-4xl text-slate-300" aria-hidden="true"></i>
        <h3 class="m-0 text-base font-bold text-slate-900">Nessuna stagione corrente</h3>
        <p class="mb-5 mt-1.5 max-w-md text-sm text-slate-500">Crea una stagione e rendila corrente per iniziare a raccogliere le iscrizioni.</p>
        <a class="btn btn-primary" [routerLink]="['/companies/categories']">Vai a stagioni e categorie</a>
    </div>

    <div *ngIf="!loading && loadError"
         class="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
        <i class="bi bi-exclamation-triangle mb-3 text-4xl text-amber-500" aria-hidden="true"></i>
        <h3 class="m-0 text-base font-bold text-slate-900">Impossibile caricare le iscrizioni</h3>
        <p class="mb-5 mt-1.5 max-w-md text-sm text-slate-500">Controlla la connessione e riprova.</p>
        <button type="button" class="btn btn-primary" (click)="load()">Riprova</button>
    </div>

    <ng-container *ngIf="loading || (season && !loadError)">
        <!-- KPIs -->
        <section class="mb-7 grid grid-cols-2 gap-4 xl:grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,1.5fr)]" aria-label="Riepilogo">
            <ng-container *ngIf="!loading; else kpiSkeleton">
                <div class="flex min-w-0 flex-col gap-1 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
                    <span class="text-xs font-bold uppercase tracking-wide text-slate-500">Iscritti</span>
                    <strong class="text-2xl font-bold tabular-nums text-slate-900">{{ stats.total }}</strong>
                    <span class="truncate text-xs text-slate-400">in {{ groups.length }} {{ groups.length === 1 ? 'categoria' : 'categorie' }}</span>
                </div>
                <div class="flex min-w-0 flex-col gap-1 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
                    <span class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <i class="h-2 w-2 rounded-full bg-emerald-500"></i>Pagati
                    </span>
                    <strong class="text-2xl font-bold tabular-nums text-slate-900">{{ stats.paid }}</strong>
                    <span class="truncate text-xs text-slate-400">{{ percent(stats.paid, stats.total) }}% degli iscritti</span>
                </div>
                <div class="flex min-w-0 flex-col gap-1 rounded-2xl border px-5 py-4 shadow-sm"
                     [ngClass]="stats.verifyingPayment ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-white'">
                    <span class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <i class="h-2 w-2 rounded-full bg-amber-500"></i>Da verificare
                    </span>
                    <strong class="text-2xl font-bold tabular-nums text-slate-900">{{ stats.verifyingPayment }}</strong>
                    <span class="truncate text-xs text-slate-400">{{ stats.verifyingPayment ? 'Pagamenti da confermare' : 'Nessun pagamento in sospeso' }}</span>
                </div>
                <div class="flex min-w-0 flex-col gap-1 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
                    <span class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <i class="h-2 w-2 rounded-full bg-red-500"></i>In attesa
                    </span>
                    <strong class="text-2xl font-bold tabular-nums text-slate-900">{{ stats.pendingPayment }}</strong>
                    <span class="truncate text-xs text-slate-400">Non ancora pagato</span>
                </div>
                <div class="col-span-2 flex min-w-0 flex-col gap-1 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm xl:col-span-1">
                    <span class="text-xs font-bold uppercase tracking-wide text-slate-500">Incassato</span>
                    <strong class="text-2xl font-bold tabular-nums text-slate-900">{{ formatEuro(stats.collected) }}</strong>
                    <div class="my-1 h-1.5 overflow-hidden rounded-full bg-slate-100" role="progressbar"
                         [attr.aria-valuenow]="percent(stats.collected, stats.expected)" aria-valuemin="0" aria-valuemax="100">
                        <span class="block h-full rounded-full bg-emerald-500" [style.width]="barWidth(stats.collected, stats.expected)"></span>
                    </div>
                    <span class="truncate text-xs text-slate-400">su {{ formatEuro(stats.expected) }} previsti · {{ stats.withoutMembership }} non tesserati</span>
                </div>
            </ng-container>
            <ng-template #kpiSkeleton>
                <div *ngFor="let i of [1, 2, 3, 4, 5]; let last = last"
                     class="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm"
                     [ngClass]="{'col-span-2 xl:col-span-1': last}">
                    <span class="h-3 w-20 animate-pulse rounded bg-slate-100"></span>
                    <span class="h-7 w-14 animate-pulse rounded bg-slate-100"></span>
                    <span class="h-3 w-32 animate-pulse rounded bg-slate-100"></span>
                </div>
            </ng-template>
        </section>

        <!-- Toolbar -->
        <div *ngIf="loading || hasCategories" class="mb-6 flex flex-wrap items-center gap-x-5 gap-y-3">
            <input type="search" class="form-control form-control-solid !w-auto min-w-[12rem] flex-[0_1_20rem]"
                   placeholder="Cerca categoria" [(ngModel)]="search" [disabled]="loading">
            <label class="m-0 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-600">
                <input type="checkbox" class="h-4 w-4 accent-[var(--primary-color)]" [(ngModel)]="hideEmpty" [disabled]="loading">
                Nascondi categorie senza iscritti
            </label>
            <div class="ml-auto flex gap-4 text-xs text-slate-500" aria-hidden="true">
                <span class="inline-flex items-center gap-1.5"><i class="h-2 w-2 rounded-full bg-emerald-500"></i>Pagati</span>
                <span class="inline-flex items-center gap-1.5"><i class="h-2 w-2 rounded-full bg-amber-500"></i>Da verificare</span>
                <span class="inline-flex items-center gap-1.5"><i class="h-2 w-2 rounded-full bg-red-500"></i>In attesa</span>
            </div>
        </div>

        <!-- Categories -->
        <ng-container *ngIf="!loading; else groupsSkeleton">
            <div *ngIf="!hasCategories"
                 class="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
                <i class="bi bi-diagram-3 mb-3 text-4xl text-slate-300" aria-hidden="true"></i>
                <h3 class="m-0 text-base font-bold text-slate-900">Nessuna categoria in questa stagione</h3>
                <p class="mb-5 mt-1.5 max-w-md text-sm text-slate-500">Crea le categorie e le quote di iscrizione per la stagione {{ season?.name }}.</p>
                <a class="btn btn-primary" [routerLink]="['/companies/categories']">Crea categorie</a>
            </div>

            <p *ngIf="hasCategories && !visibleGroups.length" class="py-8 text-center text-slate-400">
                Nessuna categoria corrisponde ai filtri.
            </p>

            <section *ngFor="let group of visibleGroups; trackBy: trackByCategory" class="mb-8">
                <header class="mb-3 flex items-baseline gap-3">
                    <h2 class="m-0 text-base font-bold text-slate-900">{{ group.parent.name }}</h2>
                    <span class="text-sm font-semibold text-slate-400">{{ group.stats.total }} {{ group.stats.total === 1 ? 'iscritto' : 'iscritti' }}</span>
                </header>

                <div class="grid grid-cols-[repeat(auto-fill,minmax(15.5rem,1fr))] gap-4">
                    <a *ngFor="let item of group.children; trackBy: trackByCategory"
                       class="group flex flex-col gap-3 rounded-2xl border border-slate-100 px-5 py-4 text-inherit no-underline shadow-sm transition hover:-translate-y-px hover:border-primary hover:shadow-md"
                       [ngClass]="item.stats.total ? 'bg-white' : 'bg-slate-50/60'"
                       [routerLink]="['/companies/subscriptions/category', item.category.id]">
                        <div class="flex items-start justify-between gap-2">
                            <h3 class="m-0 break-words text-[0.95rem] font-bold text-slate-900">{{ item.category.name }}</h3>
                            <i class="bi bi-chevron-right text-slate-300 transition-colors group-hover:text-primary" aria-hidden="true"></i>
                        </div>

                        <div class="flex flex-wrap items-baseline gap-1.5">
                            <strong class="text-3xl font-bold leading-none tabular-nums"
                                    [ngClass]="item.stats.total ? 'text-slate-900' : 'text-slate-300'">{{ item.stats.total }}</strong>
                            <span class="text-sm text-slate-500">{{ item.stats.total === 1 ? 'iscritto' : 'iscritti' }}</span>
                            <span *ngIf="item.stats.verifyingPayment"
                                  class="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[0.7rem] font-bold text-amber-800">
                                {{ item.stats.verifyingPayment }} da verificare
                            </span>
                        </div>

                        <div class="flex h-1.5 overflow-hidden rounded-full bg-slate-100"
                             [attr.aria-label]="item.stats.paid + ' pagati, ' + item.stats.verifyingPayment + ' da verificare, ' + item.stats.pendingPayment + ' in attesa'">
                            <span class="h-full bg-emerald-500" [style.width]="barWidth(item.stats.paid, item.stats.total)"></span>
                            <span class="h-full bg-amber-500" [style.width]="barWidth(item.stats.verifyingPayment, item.stats.total)"></span>
                            <span class="h-full bg-red-500" [style.width]="barWidth(item.stats.pendingPayment, item.stats.total)"></span>
                        </div>

                        <dl *ngIf="item.stats.total; else noEnrollments" class="m-0 grid grid-cols-3 gap-2">
                            <div class="min-w-0">
                                <dt class="text-[0.68rem] font-bold uppercase tracking-wide text-slate-400">Pagati</dt>
                                <dd class="m-0 truncate text-sm font-semibold tabular-nums text-slate-700">{{ item.stats.paid }}</dd>
                            </div>
                            <div class="min-w-0">
                                <dt class="text-[0.68rem] font-bold uppercase tracking-wide text-slate-400">In attesa</dt>
                                <dd class="m-0 truncate text-sm font-semibold tabular-nums text-slate-700">{{ item.stats.pendingPayment }}</dd>
                            </div>
                            <div class="min-w-0">
                                <dt class="text-[0.68rem] font-bold uppercase tracking-wide text-slate-400">Incassato</dt>
                                <dd class="m-0 truncate text-sm font-semibold tabular-nums text-slate-700">{{ formatEuro(item.stats.collected) }}</dd>
                            </div>
                        </dl>
                        <ng-template #noEnrollments>
                            <p class="m-0 text-[0.82rem] text-slate-400">Ancora nessun iscritto · quota {{ formatEuro(item.category.fullPrice) }}</p>
                        </ng-template>
                    </a>
                </div>
            </section>
        </ng-container>

        <ng-template #groupsSkeleton>
            <section *ngFor="let cards of skeletonGroups" class="mb-8" aria-hidden="true">
                <span class="mb-4 block h-4 w-40 animate-pulse rounded bg-slate-100"></span>
                <div class="grid grid-cols-[repeat(auto-fill,minmax(15.5rem,1fr))] gap-4">
                    <div *ngFor="let card of cards" class="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm">
                        <span class="h-4 w-3/5 animate-pulse rounded bg-slate-100"></span>
                        <span class="h-7 w-20 animate-pulse rounded bg-slate-100"></span>
                        <span class="h-1.5 w-full animate-pulse rounded-full bg-slate-100"></span>
                        <span class="h-7 w-4/5 animate-pulse rounded bg-slate-100"></span>
                    </div>
                </div>
            </section>
        </ng-template>
    </ng-container>
</div>
